import {
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  inject,
} from "@angular/core";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {WorkLongPressDirective} from "src/app/modules/shared/directives/work-long-press.directive";
import {ScreenSizeService} from "src/app/modules/shared/services/screenSize.service";
import {DiscussionTap} from "../interfaces/discussion-reaction.interface";
import {
  DISCUSSION_DOUBLE_TAP_DISTANCE_PX,
  DISCUSSION_DOUBLE_TAP_MS,
} from "../constants/discussion-reactions.constants";

@Directive({
  selector: "[appDiscussionGesture]",
  hostDirectives: [WorkLongPressDirective],
})
export class DiscussionGestureDirective {
  private readonly press = inject(WorkLongPressDirective);
  private readonly destroyRef = inject(DestroyRef);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly screenSize = toSignal(
    inject(ScreenSizeService).screenSize$,
    {
      requireSync: true,
    }
  );
  @Output() reactionHold = new EventEmitter<void>();
  @Output() reactionGem = new EventEmitter<void>();
  private lastTap?: DiscussionTap;
  private expiry?: ReturnType<typeof setTimeout>;
  private listening = false;

  @HostBinding("class.any-pointer-coarse:select-none")
  get preventTouchSelection(): boolean {
    return this.screenSize().isMobile && this.press.workLongPressEnabled;
  }

  @HostBinding("style.touch-action")
  get touchAction(): string | null {
    return this.press.workLongPressEnabled ? "manipulation" : null;
  }

  @Input() set appDiscussionGesture(enabled: boolean) {
    this.press.workLongPressEnabled = enabled;
    if (!enabled) this.cancel();
  }

  constructor() {
    this.press.workLongPressPreserveSelection = true;
    this.press.workPressingChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((pressing) => {
        if (!pressing) return;
        clearTimeout(this.expiry);
        this.listen();
      });
    this.press.workTapped
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (window.getSelection()?.toString()) {
          this.cancel();
          return;
        }
        const time = performance.now();
        const previous = this.lastTap;
        this.lastTap = {time, x: event.clientX, y: event.clientY};
        if (
          previous &&
          time - previous.time <= DISCUSSION_DOUBLE_TAP_MS &&
          Math.hypot(event.clientX - previous.x, event.clientY - previous.y) <=
            DISCUSSION_DOUBLE_TAP_DISTANCE_PX
        ) {
          this.cancel();
          this.reactionGem.emit();
          return;
        }
        this.expiry = setTimeout(this.cancel, DISCUSSION_DOUBLE_TAP_MS);
      });
    this.press.workGestureCancelled
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.cancel);
    this.press.workLongPressed
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reactionHold.emit());
    this.destroyRef.onDestroy(this.cancel);
  }

  private readonly outside = (event: PointerEvent): void => {
    if (
      !event.isPrimary ||
      !(event.target instanceof Node) ||
      !this.element.nativeElement.contains(event.target)
    )
      this.cancel();
  };

  private readonly cancel = (): void => {
    this.lastTap = undefined;
    clearTimeout(this.expiry);
    this.press.cancelPress();
    document.removeEventListener("scroll", this.cancel, true);
    document.removeEventListener("visibilitychange", this.cancel);
    document.removeEventListener("pointerdown", this.outside, true);
    this.listening = false;
  };

  private listen(): void {
    if (this.listening) return;
    this.listening = true;
    document.addEventListener("scroll", this.cancel, true);
    document.addEventListener("visibilitychange", this.cancel);
    document.addEventListener("pointerdown", this.outside, true);
  }
}
